import React, { useEffect, useState } from "react";
import FoodCard from "../Components/FoodCard";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { useLocation, Link } from "react-router-dom";
import {
  XMarkIcon,
  AdjustmentsHorizontalIcon,
  ChevronRightIcon,
  ShareIcon,
} from "@heroicons/react/24/outline";

const categoriesList = [
  "Electronics",
  "Food",
  "Fashion",
  "Home & Garden",
  "Sports",
  "Books",
  "Toys",
  "Beauty",
  "Automotive",
];

const SearchResults = () => {
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("relevance");
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const query = params.get("q")?.toLowerCase() || "";

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const querySnapshot = await getDocs(collection(db, "products"));
        const items = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(items);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    if (!query && !selectedCategory) {
      setFiltered([]);
      return;
    }

    let filteredItems = [];

    if (selectedCategory) {
      filteredItems = products.filter(
        (p) => p.category?.toLowerCase() === selectedCategory.toLowerCase()
      );
    } else {
      filteredItems = products.filter(
        (p) =>
          p.name?.toLowerCase().includes(query) ||
          p.category?.toLowerCase().includes(query)
      );
    }

    if (sortBy === "price-asc") {
      filteredItems.sort((a, b) => a.price - b.price);
    } else if (sortBy === "price-desc") {
      filteredItems.sort((a, b) => b.price - a.price);
    } else if (sortBy === "name-asc") {
      filteredItems.sort((a, b) => a.name.localeCompare(b.name));
    }

    setFiltered(filteredItems);
  }, [query, products, sortBy, selectedCategory]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-8 text-center text-gray-500">
        Loading products...
      </div>
    );
  }

  if (!query && !selectedCategory) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center text-gray-600">
        Please enter a search query or select a category to find products.
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-3 flex flex-col md:flex-row gap-6 relative">
      {/* Overlay */}
      {filterOpen && (
        <div
          onClick={() => setFilterOpen(false)}
          className="fixed inset-0 bg-black bg-opacity-40 z-20 md:hidden"
          aria-hidden="true"
        />
      )}

      {/* Sidebar filters */}
      <aside
        className={`bg-white border rounded-md p-4 fixed top-16 left-0 z-30 transform transition-transform duration-300
          ${
            filterOpen
              ? "translate-x-0 w-3/5 shadow-lg"
              : "-translate-x-full w-3/5"
          }
          md:static md:translate-x-0 md:w-64 md:shadow-none
        `}
      >
        <div className="flex items-center justify-between mb-6 md:hidden">
          <h3 className="text-lg font-semibold">Filters</h3>
          <button
            onClick={() => setFilterOpen(false)}
            aria-label="Close filters"
            className="p-2 rounded hover:bg-gray-100"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div>
          <h4 className="font-semibold mb-2 flex items-center gap-1 cursor-pointer">
            <AdjustmentsHorizontalIcon className="w-5 h-5" />
            Categories
          </h4>
          <ul>
            <li
              className={`cursor-pointer mb-1 px-2 py-1 rounded ${
                !selectedCategory
                  ? "bg-indigo-600 text-white font-semibold"
                  : "hover:bg-indigo-100"
              }`}
              onClick={() => setSelectedCategory(null)}
            >
              All Categories
            </li>
            {categoriesList.map((cat) => (
              <li
                key={cat}
                className={`cursor-pointer mb-1 px-2 py-1 rounded ${
                  selectedCategory === cat
                    ? "bg-indigo-600 text-white font-semibold"
                    : "hover:bg-indigo-100"
                }`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </li>
            ))}
          </ul>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0">
        {/* Breadcrumb */}
        <nav className="text-sm mb-4 text-gray-600 flex flex-wrap items-center gap-1">
          <Link to="/" className="hover:text-indigo-600">
            Home
          </Link>
          <ChevronRightIcon className="w-4 h-4" />
          <span>Search</span>
          {selectedCategory && (
            <>
              <ChevronRightIcon className="w-4 h-4" />
              <span className="font-semibold">{selectedCategory}</span>
            </>
          )}
        </nav>

        {/* Header & controls */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
          <h2 className="text-3xl font-bold text-gray-800">
            Search results for{" "}
            {selectedCategory ? (
              <span className="text-indigo-600">{selectedCategory}</span>
            ) : (
              <span className="text-indigo-600">"{query}"</span>
            )}
          </h2>

          <div className="flex items-center gap-3">
            {/* Filter toggle button (mobile) */}
            <button
              onClick={() => setFilterOpen(true)}
              className="md:hidden flex items-center gap-1 px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
            >
              <AdjustmentsHorizontalIcon className="w-5 h-5" />
              Filters
            </button>

            {/* Sort dropdown */}
            <select
              aria-label="Sort products"
              className="border rounded px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="relevance">Sort by Relevance</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="name-asc">Name: A to Z</option>
            </select>
          </div>
        </div>

        {/* Products grid or no results message */}
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-700 text-lg font-semibold">
            No products found{" "}
            {selectedCategory
              ? `in category "${selectedCategory}"`
              : `for "${query}"`}
            .
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {filtered.map((product) => (
              <FoodCard key={product.id} {...product} />
            ))}
          </div>
        )}

        {/* Pagination placeholder */}
        {filtered.length > 0 && (
          <div className="mt-8 flex justify-between items-center text-gray-600">
            <span>
              Showing {filtered.length}{" "}
              {filtered.length === 1 ? "product" : "products"}
            </span>

            <div className="flex gap-4 items-center">
              <button
                disabled
                className="px-4 py-2 border rounded text-gray-400 cursor-not-allowed"
              >
                Previous
              </button>
              <button className="px-4 py-2 border rounded hover:bg-indigo-600 hover:text-white transition">
                Next
              </button>
            </div>
          </div>
        )}

        {/* Share & help section */}
        <div className="mt-10 border-t pt-6 flex flex-wrap justify-between items-center gap-4 text-gray-600">
          <div className="flex items-center gap-2">
            <ShareIcon className="w-6 h-6" />
            <span>Share this search:</span>
            <a
              href="#"
              className="hover:text-indigo-600 underline"
              aria-label="Share on Facebook"
              title="Facebook"
            >
              Facebook
            </a>
            <a
              href="#"
              className="hover:text-indigo-600 underline"
              aria-label="Share on Twitter"
              title="Twitter"
            >
              Twitter
            </a>
            <a
              href="#"
              className="hover:text-indigo-600 underline"
              aria-label="Share on LinkedIn"
              title="LinkedIn"
            >
              LinkedIn
            </a>
          </div>

          <div>
            <Link
              to="/contact"
              className="text-indigo-600 hover:underline font-semibold"
            >
              Need help? Contact us
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SearchResults;
